import { OptimizationResults, OptimizationResultsWithStats } from '../types';
import { minMaxNormalize } from './normalization';
import {
  RESULT_ALGORITHM_KEYS as ALGORITHM_KEYS,
  RESULT_ALGORITHM_NAMES,
  ResultAlgorithmKey as AlgorithmKey,
} from './algorithmRegistry';

export { ALGORITHM_KEYS };
export type { AlgorithmKey };
export const ALGORITHM_NAMES: Record<AlgorithmKey, string> = RESULT_ALGORITHM_NAMES;

export type QTISummaryMode = 'raw' | 'mean' | 'median';

export interface QTIInputRow {
  algorithmKey: AlgorithmKey;
  algorithmName?: string;
  fitness: number;
  executionTime?: number;
}

export interface QTIRow {
  algorithmKey: AlgorithmKey;
  algorithmName: string;
  fitness: number;
  executionTime: number;
  executionTimeNorm: number;
  qti: number;
}

export interface QTITaskRow {
  taskCount: number;
  entries: QTIRow[];
}

function isFiniteNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function calculateQTIFromEntries(entries: QTIInputRow[]): QTIRow[] {
  const validExecutionTimes = entries
    .map((entry) => entry.executionTime)
    .filter(isFiniteNumber);

  const minExecutionTime = validExecutionTimes.length > 0 ? Math.min(...validExecutionTimes) : NaN;
  const maxExecutionTime = validExecutionTimes.length > 0 ? Math.max(...validExecutionTimes) : NaN;

  return entries.map((entry) => {
    const algorithmName = entry.algorithmName ?? ALGORITHM_NAMES[entry.algorithmKey];
    const executionTime = isFiniteNumber(entry.executionTime) ? entry.executionTime : NaN;
    const hasValidMetrics = Number.isFinite(entry.fitness) && Number.isFinite(executionTime);
    const executionTimeNorm =
      hasValidMetrics && Number.isFinite(minExecutionTime) && Number.isFinite(maxExecutionTime)
        ? minMaxNormalize(executionTime, minExecutionTime, maxExecutionTime)
        : NaN;
    const qti =
      hasValidMetrics && Number.isFinite(executionTimeNorm)
        ? entry.fitness / (1 + executionTimeNorm)
        : NaN;

    return {
      algorithmKey: entry.algorithmKey,
      algorithmName,
      fitness: entry.fitness,
      executionTime,
      executionTimeNorm,
      qti,
    };
  });
}

export function calculateQTIForResults(
  results: OptimizationResults | OptimizationResultsWithStats,
  summaryMode: QTISummaryMode = 'raw'
): QTIRow[] {
  const useStatistics = summaryMode !== 'raw' && 'statistics' in results && results.statistics !== undefined;

  const entries: QTIInputRow[] = ALGORITHM_KEYS.reduce<QTIInputRow[]>((acc, algorithmKey) => {
    if (useStatistics) {
      const stats = results.statistics?.[algorithmKey];
      if (!stats) {
        return acc;
      }
      acc.push({
        algorithmKey,
        fitness: stats.fitness[summaryMode],
        executionTime: stats.executionTime[summaryMode],
      });
      return acc;
    }

    const algorithmResult = results[algorithmKey];
    if (!algorithmResult) {
      return acc;
    }
    acc.push({
      algorithmKey,
      fitness: algorithmResult.fitness,
      executionTime: algorithmResult.executionTime,
    });
    return acc;
  }, []);

  return calculateQTIFromEntries(entries);
}

export function calculateAverageQTI(taskRows: QTITaskRow[]): Array<{
  algorithmKey: AlgorithmKey;
  algorithmName: string;
  averageQTI: number;
}> {
  const algorithmKeys = Array.from(
    new Set(taskRows.flatMap((taskRow) => taskRow.entries.map((entry) => entry.algorithmKey)))
  ) as AlgorithmKey[];

  return algorithmKeys.map((algorithmKey) => {
    const qtiValues = taskRows
      .map((taskRow) => taskRow.entries.find((entry) => entry.algorithmKey === algorithmKey)?.qti ?? NaN)
      .filter((value) => Number.isFinite(value));

    const averageQTI =
      qtiValues.length > 0
        ? qtiValues.reduce((sum, value) => sum + value, 0) / qtiValues.length
        : NaN;

    return {
      algorithmKey,
      algorithmName: ALGORITHM_NAMES[algorithmKey],
      averageQTI,
    };
  });
}

export function calculateQTIExceptions(
  taskRows: QTITaskRow[],
  baselineAlgorithm: AlgorithmKey = 'sflaga'
): Array<{
  taskCount: number;
  algorithmKey: AlgorithmKey;
  algorithmName: string;
  qti: number;
  baselineQTI: number;
}> {
  const exceptions: Array<{
    taskCount: number;
    algorithmKey: AlgorithmKey;
    algorithmName: string;
    qti: number;
    baselineQTI: number;
  }> = [];

  taskRows.forEach((taskRow) => {
    const baseline = taskRow.entries.find((entry) => entry.algorithmKey === baselineAlgorithm);
    if (!baseline || !Number.isFinite(baseline.qti)) {
      return;
    }

    taskRow.entries.forEach((entry) => {
      if (
        entry.algorithmKey !== baselineAlgorithm &&
        Number.isFinite(entry.qti) &&
        entry.qti > baseline.qti
      ) {
        exceptions.push({
          taskCount: taskRow.taskCount,
          algorithmKey: entry.algorithmKey,
          algorithmName: entry.algorithmName,
          qti: entry.qti,
          baselineQTI: baseline.qti,
        });
      }
    });
  });

  return exceptions;
}
